import React from 'react';
import { styled } from '@linaria/react';

import {
  DemoRadioControl,
  DemoSliderControl,
} from '@/components/Demo';

import FlexDemo from './FlexDemo';
import Output from './Output';
import Item from './Item';
import Dl from './Dl';

function WrappingIntro() {
  const [scale, setScale] = React.useState(1);
  const freshScale = React.useRef();
  freshScale.current = scale;

  React.useEffect(() => {
    function calculateScale() {
      const windowWidth = window.innerWidth;
      const requiredSpace = 616 + 16 * 2 + 32;

      if (windowWidth < requiredSpace) {
        setScale(windowWidth / requiredSpace);
      } else if (freshScale.current !== 1) {
        setScale(1);
      }
    }

    calculateScale();

    window.addEventListener('resize', calculateScale);

    return () => {
      window.removeEventListener('resize', calculateScale);
    };
  }, []);

  return (
    <FlexDemo
      centered
      controlPosition="top"
      initialValues={{
        '--wrap': 'nowrap',
        '--justify': 'center',
        containerWidth: 600,
      }}
      controls={[
        <DemoRadioControl
          key="--wrap"
          id="--wrap"
          label="flex-wrap"
          options={[
            { label: 'nowrap (default)', value: 'nowrap' },
            'wrap',
          ]}
          wrapperStyle={{
            width: 284,
          }}
        />,
        <DemoSliderControl
          key="containerWidth"
          id="containerWidth"
          label="Container width"
          min={150 - 8}
          max={600}
          handleFocusColor="white"
          previewRenderer={(val) => {
            if (val > 360) {
              return val;
            } else if (val > 180) {
              return val + 4;
            } else {
              return val + 8;
            }
          }}
          wrapperStyle={{
            width: 284,
          }}
        />,
      ]}
    >
      {({ containerWidth, ...state }) => {
        return (
          <ScaleWrapper
            style={{
              width: 616,
              transform: `scale(${scale})`,
            }}
          >
            <Output
              state={state}
              height="auto"
              wrapperStyle={{ width: containerWidth + 16 }}
            >
              <WrapItem
                scale={scale}
                containerWidth={containerWidth}
                flexWrap={state['--wrap']}
              />
              <WrapItem
                scale={scale}
                containerWidth={containerWidth}
                flexWrap={state['--wrap']}
              />
              <WrapItem
                scale={scale}
                containerWidth={containerWidth}
                flexWrap={state['--wrap']}
              />
            </Output>
          </ScaleWrapper>
        );
      }}
    </FlexDemo>
  );
}

function WrapItem({
  scale,
  flexBasis = 180,
  flexWrap,
  containerWidth,
  ...delegated
}) {
  const ref = React.useRef();

  const [actualWidth, setActualWidth] = React.useState(null);

  React.useEffect(() => {
    if (!ref.current) {
      return;
    }

    const firstBB = ref.current.getBoundingClientRect();

    setActualWidth(Math.round(firstBB.width * (1 / scale)));
  }, [scale, flexWrap, containerWidth]);

  return (
    <Item
      ref={ref}
      layout={false}
      style={{ minHeight: 200, flexBasis }}
      textWrapperStyle={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
      }}
      {...delegated}
    >
      <Dl>
        <dt data-prop>flex-basis</dt>
        <dd>{flexBasis}px</dd>
        {typeof actualWidth === 'number' && (
          <>
            <dt>Actual size</dt>
            <dd>{actualWidth}px</dd>
          </>
        )}
      </Dl>
    </Item>
  );
}

const ScaleWrapper = styled.div`
  display: flex;
  justify-content: center;
  transform-origin: top center;
`;

export default WrappingIntro;
